import { getOrderTiming, parsePrepMinutes, parseReceivedTime } from "../KDS/orderTiming";
import { isLiveTask } from "../KDS/kitchenActions";

const id = (value) => value == null ? "" : String(value).trim();
const rank = { neutral: 0, green: 1, orange: 2, red: 3 };

// Keep the raw KDS min:sec duration for urgency comparisons; format only the label.
export function formatKitchenDelay(display) {
  if (typeof display !== "string") return "";
  const match = /^(\d+):([0-5]\d)$/.exec(display);
  if (!match) return display;
  const minutes = Number(match[1]);
  if (!Number.isSafeInteger(minutes)) return display;
  if (minutes === 0) return Number(match[2]) > 0 ? "<1 min" : "0 mins";
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const minuteLabel = `${remainder} ${remainder === 1 ? "min" : "mins"}`;
  return hours
    ? `${hours} ${hours === 1 ? "hr" : "hrs"}${remainder ? ` ${minuteLabel}` : ""}`
    : minuteLabel;
}

export function getTableServingTiming(table, jobs = [], now = Date.now()) {
  if (table?.status !== "OCCUPIED") return null;
  const aliases = [table?.metadata?.tableMetadataId, table?.mapId].map(id).filter(Boolean);
  const cartId = id(table?.statusCartId);
  const orderId = id(table?.statusOrderId);
  const active = jobs.filter((job) => {
    if (!["PENDING", "IN_PROGRESS"].includes(job?.status)) return false;
    if (!(job.tasks || []).some((task) => isLiveTask(task) && Number(task.quantity) > 0)) return false;
    const metadata = job.metadata || {};
    const tableId = id(metadata.tableId ?? metadata.tableMapId ?? metadata.tableMetadataId);
    if (["TAKEAWAY", "DELIVERY"].includes(tableId.toUpperCase()) ||
      (metadata.subOrderType && !String(metadata.subOrderType).includes("DINE_IN"))) return false;
    const jobCart = id(metadata.cartId);
    if (cartId && jobCart && cartId !== jobCart) return false;
    // Never match display numbers alone: floors and areas can repeat table names.
    if (tableId) return aliases.includes(tableId);
    return !!((cartId && jobCart === cartId) ||
      (orderId && id(job.externalRefId) === orderId));
  });
  if (!active.length) return null;
  let worst = { tone: "neutral", display: "", description: "No valid receipt time or prep estimate." };
  let delayedOrders = 0;
  let unknownOrders = 0;
  active.forEach((job) => {
    const timing = getOrderTiming({ created: job.created, prepTime: job.metadata?.avgPrepTime }, now);
    if (timing.tone === "red") delayedOrders += 1;
    if (timing.tone === "neutral") unknownOrders += 1;
    // Within a severity, greatest overdue/least time remaining wins.
    const seconds = timing.display === "—" ? Infinity : timing.display.split(":").reduce((m, s) => m * 60 + Number(s), 0);
    const urgency = timing.tone === "red" ? -seconds : seconds;
    if (rank[timing.tone] > rank[worst.tone] ||
      (timing.tone === worst.tone && urgency < (worst.urgency ?? Infinity))) worst = { ...timing, urgency };
  });
  const label = { green: "On time", orange: "Due soon", red: "Delayed", neutral: "Timing unavailable" }[worst.tone];
  const timeText = worst.tone === "red" ? formatKitchenDelay(worst.display) : worst.display;
  return {
    ...worst, display: worst.display === "—" ? "" : worst.display,
    label, delayedOrders, unknownOrders, orderCount: active.length,
    description: `Kitchen preparation timing, not table occupancy or confirmed service. ${delayedOrders} of ${active.length} active kitchen orders delayed. ${worst.tone === "red" ? "Overdue by" : "Time remaining"} ${timeText || "unavailable"}${worst.tone !== "red" && timeText ? " (minutes:seconds)" : ""}.${unknownOrders ? ` ${unknownOrders} orders have unavailable timing.` : ""}`,
  };
}

const DROPPED_TASK_STATUSES = ["REMOVED", "CANCELLED", "CANCELED", "REJECTED"];
// COMPLETED / READY_TO_SERVE = kitchen finished preparing (not yet served); SERVED = delivered to the table.
const PREPARED_TASK_STATUSES = ["COMPLETED", "READY_TO_SERVE"];

// Kitchen status from the `jobs` returned by getTableStatusByCart (stored on the table as kitchenJobs).
// Priority: any task IN_PROGRESS → countdown to the longest prep time; any PENDING → not accepted;
// all SERVED → served; all COMPLETED / READY_TO_SERVE (or served) → ready to serve.
export function getTableKitchenStatus(table, now = Date.now()) {
  if (table?.status !== "OCCUPIED") return null;
  // Jobs are deleted once all their tasks finish — fall back to the cart's productStatuses.
  if (!table?.kitchenJobs?.length) {
    const readyToServe = (table?.productStatuses || []).some(
      (p) => p?.status === "READY_TO_SERVE" && Number(p?.count) > 0
    );
    return readyToServe
      ? { tone: "green", label: "Ready to serve", display: "", description: "Kitchen has finished preparing; items are ready to serve." }
      : null;
  }
  const tasks = (table?.kitchenJobs || [])
    .flatMap((job) => (job?.tasks || []).map((task) => ({ task, job })))
    .filter(({ task }) => task && !DROPPED_TASK_STATUSES.includes(task.taskStatus));
  if (!tasks.length) return null;

  const inProgress = tasks.filter(({ task }) => task.taskStatus === "IN_PROGRESS");
  if (inProgress.length) {
    // Timer starts at kitchen receipt (job.created), same as KDS. The item finishing last wins.
    let latest = null;
    inProgress.forEach(({ task, job }) => {
      const prepTime = parsePrepMinutes(task?.metadata?.prepTime);
      const received = parseReceivedTime(job?.created);
      if (prepTime == null || received == null) return;
      const deadline = received + prepTime * 60000;
      if (!latest || deadline > latest.deadline) latest = { deadline, prepTime, created: job.created };
    });
    if (!latest) {
      return { tone: "neutral", label: "Preparing", display: "", description: "Kitchen is preparing. Prep time unavailable." };
    }
    const timing = getOrderTiming({ created: latest.created, prepTime: latest.prepTime }, now);
    const label = { green: "On time", orange: "Due soon", red: "Delayed" }[timing.tone] || "Preparing";
    const timeText = timing.tone === "red" ? formatKitchenDelay(timing.display) : timing.display;
    return {
      tone: timing.tone,
      label,
      display: timing.display === "—" ? "" : timing.display,
      description: `Kitchen is preparing ${inProgress.length} item(s), longest prep time ${latest.prepTime} mins. ${timing.tone === "red" ? "Overdue by" : "Time remaining"} ${timeText}${timing.tone !== "red" ? " (minutes:seconds)" : ""}.`,
    };
  }
  if (tasks.some(({ task }) => task.taskStatus === "PENDING")) {
    return { tone: "orange", label: "Kitchen not accepted", display: "", description: "Kitchen has not accepted the order yet." };
  }
  if (tasks.every(({ task }) => task.taskStatus === "SERVED")) {
    return { tone: "neutral", label: "Served", display: "", description: "All items have been served." };
  }
  if (tasks.every(({ task }) => task.taskStatus === "SERVED" || PREPARED_TASK_STATUSES.includes(task.taskStatus))) {
    return { tone: "green", label: "Ready to serve", display: "", description: "Kitchen has finished preparing; items are ready to serve." };
  }
  return null;
}
